<template>
  <div>
    <ContentLoader
      ref="contentLoader"
      :key="rerenderKey"
      class="container"
      :profile-owner="profileOwner"
      :load-fn="loadFn"
      :card-row-options="options.cardRow"
    >
      <ProfileVideosEmptyState
        v-if="options.emptyState"
        slot="empty"
        :options="options.emptyState"
      />
    </ContentLoader>
  </div>
</template>

<script>
import webcall from '@/scripts/webcall';
import { meApi } from '@/scripts/webcall/v3/me';
import { usersApi } from '@/scripts/webcall/v3/users';
import { videosApi } from '@/scripts/webcall/v3/videos';
import playlist from '@/scripts/libs/playlist';
// Components
import ContentLoader from './Playlists/ContentLoader.ts.vue';
import ProfileVideosEmptyState from './ProfileVideosEmptyState';

// v3 Compat
const extractData = r => r.data;
const v3WebcallCompat = {
  listProfileVideos: ({ params }) => usersApi.listVideos({ userVanity: params.id }, params).then(extractData),
  listProfileLikedVideos: ({ params }) => usersApi.listLikedVideos({ userVanity: params.id }, params).then(extractData),
  listLikedVideos: ({ params }) => meApi.likes.listVideos(null, params).then(extractData),
  listQueuedVideos: ({ params }) => meApi.watchLater.listVideos(null, params).then(extractData),
};

export default {
  components: {
    ContentLoader,
    ProfileVideosEmptyState,
  },
  props: {
    /**
     * Type of video to load
     */
    type: {
      type: [String, null],
      default: null,
    },

    /**
     * Parameters needed to be passe down the ProfileVideos component
     */
    profileOwner: {
      type: Object,
      required: true,
    },

    permissions: {
      type: Object,
      required: true,
    },
  },
  data() {
    // Adding data to force rerender content loader
    return {
      rerenderKey: 0,
    };
  },
  computed: {
    canShowOwnLikeVideos() {
      return this.permissions.viewAsOther && this.profileOwner.profile.settings.privacy.showLikes;
    },

    options() {
      switch (this.type) {
        case 'watch-later': {
          return {
            emptyState: {
              name: 'no-watch-later',
              title: this.$t('profile.empty_state.watch-later.title'),
              message: this.$t('profile.empty_state.watch-later.message.owner'),
            },
            cardRow: {
              actions: [{
                label: this.$t('playlist.actions.remove_from', { item: this.$t('profile.tabs.watch-later', { count: 1 }) }),
                icon: 'fa:trash-o',
                action: item => (
                  webcall.postUnqueueVideo({ video_id: item.id })
                    .then(() => {
                      this.removeContent(item);
                    })
                ),
              }, {
                // Remove watch later button
                label: this.$t('playlist.actions.watch_later'),
                permissions: {
                  show: false,
                },
              }],
            },
          };
        }

        case 'likes': {
          return {
            emptyState: {
              name: 'no-likes',
              title: this.permissions.isOwner
                ? this.$t('profile.empty_state.likes.title')
                : this.$t('profile.empty_state.default_messages')[0],
              message: this.permissions.isOwner
                ? this.$t('profile.empty_state.likes.message.owner')
                : this.$t('profile.empty_state.likes.message.other', { name: this.profileOwner.profile.firstName }),
            },
            cardRow: {
              actions: [{
                label: this.$t('playlist.actions.remove_from', { item: this.$t('profile.tabs.likes', { count: 2 }) }),
                icon: 'fa:trash-o',
                show: this.permissions.isOwner,
                action: item => (
                  webcall.postUserVideoRating({
                    rating_id: 'rating_like',
                    video_id: item.id,
                    params: {
                      rating_like: 0,
                    },
                  })
                    .then(() => {
                      this.removeContent(item);
                    })
                ),
              }],
            },
          };
        }

        case 'videos': {
          // Video has no empty state because a director must have at least 1 video
          return {
            emptyState: {
              name: 'no-playlist',
            },
          };
        }

        default: {
          return {};
        }
      }
    },

    loadFn() {
      const type = this.type;
      if (this.permissions.isOwner) {
        switch (type) {
          case 'video': return v3WebcallCompat.listProfileVideos;
          case 'likes': return v3WebcallCompat.listLikedVideos;
          case 'watch-later': return v3WebcallCompat.listQueuedVideos;
          default: {
            return null;
          }
        }
      } else {
        switch (type) {
          case 'video': return v3WebcallCompat.listProfileVideos;
          case 'likes': {
            if (this.permissions.viewAsOther) {
              return (
                this.canShowOwnLikeVideos
                  ? v3WebcallCompat.listProfileLikedVideos
                  // Disable this eslint rule as it is a false alarm
                  // eslint-disable-next-line vue/no-async-in-computed-properties
                  : () => Promise.resolve({ videos: [] })
              );
            } else {
              return v3WebcallCompat.listProfileLikedVideos;
            }
          }
          default: {
            return null;
          }
        }
      }
    },
  },
  async created() {
    if (this.$route.query.video_to_add) {
      const { data } = await videosApi.show({
        videoId: this.$route.query.video_to_add,
      });
      let added= false;
      await v3WebcallCompat.listQueuedVideos({
        params: {
          perPage: 10,
          page: 0,
          id: this.profileOwner.profile.vanity,
        },
      }).then((result) => {
        result.items.forEach((item) => {
          if (item.id===this.$route.query.video_to_add) {
            added= true;
          }
        });
      })
        .then(() => {
          if (added===false) {
            meApi.watchLater.addVideo({
              videoId: this.$route.query.video_to_add,
            }).then(() => {
              this.rerenderComponent();
            });
            playlist.openPlaylistAddedModal(
              data,
            );
            window.history.replaceState({}, 'title', '/watch-later');
          } else {
            playlist.openPlaylistAddedModal(
              data,
            );
            window.history.replaceState({}, 'title', '/watch-later');
          }
        });
    }
  },
  methods: {
    removeContent(content) {
      this.$refs.contentLoader.removeContent({ id: content.id });
    },
    rerenderComponent() {
      this.rerenderKey+=1;
    },
  },
};
</script>
